import classNames from 'classnames';
import { useIsAiGeneratedField } from 'components/common-form/contexts/SmartQuoteContext';
import { useProductData } from 'components/common-form/hooks/useData';
import { InfoIconButton } from 'components/shared/InfoIconButton';
import { useHelpCenterFeature } from 'hooks/use-help-center-feature';
import { useIsAnyRouteActive } from 'hooks/use-is-any-route-active';
import type { IntlKey } from 'locales';
import { TranslatedValidationError } from 'models/formError';
import { FC, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import { InfoTooltip } from '../InfoTooltip';
import { toColor, ValidationFeedbackMessage } from './ErrorMessages';

const FormGroup = styled.div<{ $validationError?: TranslatedValidationError }>`
  .form-control,
  sg-datepicker input-group,
  .btn-group-toggle .btn.active {
    ${({ $validationError }) =>
      $validationError ? `border-color: var(--bs-${toColor($validationError)}) !important` : ''}
    ${({ $validationError }) =>
      $validationError ? `box-shadow: inset 0 -0.0625rem 0 0 var(--bs-${toColor($validationError)})` : ''}
  }
  .form-control:hover,
  sg-datepicker .dp__wrapper.dp__wrapper--hovered .form-control {
    ${({ $validationError }) =>
      $validationError ? `box-shadow: inset 0 0 0 0.0625rem var(--bs-${toColor($validationError)})` : ''}
  }
  .form-control:focus,
  sg-datepicker .dp__wrapper.dp__wrapper .form-control.focus {
    ${({ $validationError }) =>
      $validationError ? `box-shadow: inset 0 0 0 2px var(--bs-${toColor($validationError)})` : ''}
  }
`;

type VerticalFormGroupComponentProps = Readonly<{
  children: ReactNode;
  inputId: string;
  labelText: string;
  labelContent?: ReactNode;
  className?: string;
  labelClassName?: string;
  validationError?: TranslatedValidationError;
  tooltipKey?: IntlKey;
}>;

export const VerticalFormGroupComponent: FC<VerticalFormGroupComponentProps> = ({
  inputId,
  labelText,
  labelContent,
  children,
  className,
  labelClassName,
  validationError,
  tooltipKey,
}) => {
  return (
    <FormGroup
      className={classNames('d-flex flex-column mb-3', className)}
      data-e2e={inputId}
      id={inputId}
      $validationError={validationError}
    >
      <InfoLabel
        inputId={inputId}
        labelClassName={labelClassName}
        tooltipKey={tooltipKey}
        labelContent={labelContent}
        labelText={labelText}
      />

      {children}
      <ValidationFeedbackMessage validationError={validationError} />
    </FormGroup>
  );
};

VerticalFormGroupComponent.displayName = 'VerticalFormGroupComponent';

type InfoLabelProps = {
  inputId: string;
  labelText: string;
  labelContent: ReactNode;
  labelClassName?: string;
  tooltipKey?: IntlKey;
};

function AiGeneratedLabel({ children, isAiGenerated }: PropsWithChildren<{ isAiGenerated?: boolean }>) {
  if (!isAiGenerated) {
    return children;
  }
  return (
    <div className="d-flex align-items-center justify-content-between">
      {children}
      <div
        className="badge badge-sm badge-outline-info rounded-pill align-self-start"
        title="This field has been AI Generated"
      >
        AI
      </div>
    </div>
  );
}

function InfoLabel({ inputId, labelText, labelClassName, labelContent, tooltipKey }: InfoLabelProps) {
  const isStaticOrDynamicScreen = useIsAnyRouteActive('/static', '/dynamic');
  const isAiGenerated = useIsAiGeneratedField(inputId);

  if (isStaticOrDynamicScreen) {
    return (
      <AiGeneratedLabel isAiGenerated={isAiGenerated}>
        <StaticInfoLabel
          inputId={inputId}
          labelClassName={labelClassName}
          labelContent={labelContent}
          labelText={labelText}
        />
      </AiGeneratedLabel>
    );
  }

  return (
    <AiGeneratedLabel isAiGenerated={isAiGenerated}>
      <BaseInfoLabel
        inputId={inputId}
        labelClassName={labelClassName}
        tooltipKey={tooltipKey}
        labelContent={labelContent}
        labelText={labelText}
      />
    </AiGeneratedLabel>
  );
}

function StaticInfoLabel({ inputId, labelText, labelClassName, labelContent }: InfoLabelProps) {
  const { productSubtype, productType } = useProductData();
  const { open, exists } = useHelpCenterFeature({ name: inputId, productSubtype, productType });

  return (
    <label
      htmlFor={inputId}
      data-help-center-name={inputId}
      className={classNames('form-label text-start mb-1', labelClassName)}
    >
      {labelText}
      {labelContent}
      {exists && <InfoIconButton size="sm" className="ms-2" onClick={open} />}
    </label>
  );
}

function BaseInfoLabel({ inputId, labelText, labelContent, labelClassName, tooltipKey }: InfoLabelProps) {
  const hasTooltipKey = tooltipKey !== undefined;
  return (
    <label htmlFor={inputId} className={classNames('form-label text-start mb-1', labelClassName)}>
      {labelText}
      {labelContent}
      {hasTooltipKey && <InfoTooltip id={`tooltip-${inputId}`} tooltipKey={tooltipKey} className="icon-sm" />}
    </label>
  );
}
